import type { FormInstance } from "antd";
import { Button, ConfigProvider, Form, Input, Popover, Select } from "antd";
import { MdInfoOutline } from "react-icons/md";

type FieldType = {
  type?: string;
  severity?: string;
  message?: string;
};

export const FeedbackForm = ({
  form,
  onFinish,
  isMobile = false
}: {
  form: FormInstance<FieldType>;
  onFinish: (values: FieldType) => void;
  isMobile?: boolean;
}) => {
  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            defaultHoverBorderColor: "rgb(64, 41, 117)", // 20 pxs lighter
            defaultBorderColor: "rgb(44, 21, 97)",
            defaultBg: "rgb(44, 21, 97)",
            defaultHoverBg: "rgb(64, 41, 117)" // 20 pxs lighter
          },
          Select: {
            colorBorder: "#E8EBF4",
            colorBgContainer: "white"
          },
          Input: {
            colorBorder: "#E8EBF4"
          }
        }
      }}
    >
      <div className={isMobile ? "p-4" : "p-6"}>
        <h2
          style={{ color: "rgb(44, 21, 97)" }}
          className={isMobile ? "text-xl font-semibold mb-4" : "text-2xl font-semibold mb-6"}
        >
          Report Issue
        </h2>
        <Form
          form={form}
          name="feedback"
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          className="space-y-4"
          style={{ width: isMobile ? "calc(100vw - 48px)" : "400px", maxWidth: isMobile ? "none" : "400px" }}
        >
          <Form.Item<FieldType>
            name="type"
            label={
              <span
                style={{
                  color: "rgb(44, 21, 97)",
                  display: "flex",
                  alignItems: "center"
                }}
              >
                Type{" "}
                <Popover
                  content={
                    <span>
                      <b>Not Responding</b> - The AI stopped answering or is
                      frozen.
                      <br />
                      <b>Too Slow</b> - Responses are delayed or lagging
                      significantly.
                      <br />
                      <b>Misunderstood Me</b> - The AI gave irrelevant or
                      incorrect responses.
                      <br />
                      <b>Lost Connection</b> - Network-related issues preventing
                      chat interaction.
                      <br />
                      <b>Unexpected Error</b> -A catch-all for miscellaneous or
                      unknown issues.
                    </span>
                  }
                >
                  <MdInfoOutline className="ml-1" />
                </Popover>
              </span>
            }
            rules={[{ required: true, message: "Please input issue type!" }]}
          >
            <Select
              showSearch
              placeholder="Not Responding"
              options={[
                {
                  value: "not responding",
                  label: "Not Responding"
                },
                {
                  value: "too slow",
                  label: "Too Slow"
                },
                {
                  value: "misunderstood me",
                  label: "Misunderstood Me"
                },
                {
                  value: "lost connection",
                  label: "Lost Connection"
                },
                {
                  value: "unexpected error",
                  label: "Unexpected Error"
                },
                {
                  value: "other",
                  label: "General Feedback / Other"
                }
              ]}
            />
          </Form.Item>
          <Form.Item<FieldType>
            name="severity"
            label={<span style={{ color: "rgb(44, 21, 97)" }}>Severity</span>}
            rules={[
              { required: true, message: "Please input severity level!" }
            ]}
          >
            <Select
              showSearch
              placeholder="Just a Heads-up"
              options={[
                { value: "heads up", label: "Just a Heads-up" },
                { value: "minor hiccup", label: "Minor Hiccup" },
                { value: "needs attention", label: "Needs Attention" },
                { value: "important fix", label: "Important Fix" }
              ]}
            />
          </Form.Item>

          <Form.Item<FieldType>
            name="message"
            label={<span style={{ color: "rgb(44, 21, 97)" }}>Message</span>}
            rules={[{ required: true, message: "Please input message!" }]}
          >
            <Input.TextArea rows={4} showCount />
          </Form.Item>

          <Form.Item label={null}>
            <Button
              style={{
                color: "white"
              }}
              htmlType="submit"
              className={isMobile ? "float-right mt-4 min-h-touch" : "float-right mt-4"}
            >
              Send
            </Button>
          </Form.Item>
        </Form>
      </div>
    </ConfigProvider>
  );
};
